import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, EASE } from "@/components/Reveal";
import Counter from "@/components/Counter";
import CTA from "@/components/CTA";
import { PROJECTS, PROJECT_FILTERS } from "@/data/content";

function ProjectCard({ project, index }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.6, delay: index * 0.05, ease: EASE }}
    >
      <Link
        to={`/portfolio/${project.id}`}
        data-testid={`project-card-${project.id}`}
        className="group relative block w-full text-left overflow-hidden border border-zinc-800/80 bg-void-2/40 hover:border-zinc-500 transition-colors duration-500"
      >
        <div className="relative h-64 overflow-hidden">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-void/90 via-void/20 to-transparent" />
          <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] bg-void/80 backdrop-blur px-3 py-1.5 text-zinc-300 border border-zinc-700/60">
            {project.category}
          </span>
        </div>
        <div className="p-6">
          <h3 className="font-display text-lg font-bold text-zinc-100 group-hover:text-white transition-colors leading-snug">
            {project.title}
          </h3>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] tracking-wider text-zinc-500">
            <span>{project.location}</span>
            <span className="text-[#C5A059]">{project.value}</span>
            <span>{project.status}</span>
          </div>
          {project.id !== "fountainbridge-masterplan" && (
            <p
              data-testid={`project-note-${project.id}`}
              className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-600 border-t border-zinc-800/60 pt-4"
            >
              Director experience — previous employer
            </p>
          )}
          <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 group-hover:text-zinc-200 transition-colors">
            View case study
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Portfolio() {
  const [filter, setFilter] = useState("all");
  const visible = filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.filter === filter);

  return (
    <>
      <PageHeader
        overline="Portfolio & case studies"
        lines={["Landmark projects", "& masterplans"]}
        intro="Eight schemes across regeneration, residential, healthcare, arenas and hospitality — each one delivered or led with the same hands-on standard."
        testid="portfolio-headline"
      />
      <section className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
        <Reveal>
          <div className="flex flex-wrap gap-3" data-testid="portfolio-filters">
            {PROJECT_FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={f.testid}
                onClick={() => setFilter(f.id)}
                className={`px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] border transition-colors duration-300 ${
                  filter === f.id
                    ? "bg-zinc-50 text-zinc-950 border-zinc-50"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-400 hover:text-zinc-100"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>
        <motion.div layout className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} />
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <section className="border-y border-zinc-800/60 bg-void-2/40 blueprint-grid">
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {[
            { value: 1.9, prefix: "£", suffix: "B+", decimals: 1, label: "Combined scheme GDV" },
            { value: 7015, suffix: "+", label: "Homes across all developments" },
            { value: 8500, suffix: "", label: "Arena seats in Edinburgh" },
            { value: 2, suffix: "", label: "Nations covered — UK & Ireland" },
          ].map((m, i) => (
            <Reveal key={m.label} delay={i * 0.1}>
              <div className="border-l border-zinc-800 pl-6">
                <p className="font-display text-3xl sm:text-4xl font-extrabold text-zinc-50 tracking-tight">
                  <Counter end={m.value} prefix={m.prefix || ""} suffix={m.suffix} decimals={m.decimals || 0} />
                </p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">{m.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTA title="Your project could be the next landmark" text="From £5M refurbishments to £300M masterplans — tell us what you're planning." />
    </>
  );
}
